import { useState, useEffect } from "react";
import { Button, Spin, Typography, notification } from "antd";
import { SaveOutlined } from "@ant-design/icons";
import { ThemeColors } from "./types";
import { COLOR_GROUPS, initialTheme } from "./constants";
import { useKioskConfiguration } from "./hooks/useKioskConfiguration";
import { useSaveKioskConfiguration } from "./hooks/useKioskConfigurationMutations";
import ColorField from "./ColorField";
import KioskPreview from "./KioskPreview";
import {
  PageWrapper,
  LeftPanel,
  ScrollableGroups,
  PanelHeader,
  GroupCard,
  GroupHeader,
  GroupAccentBar,
  GroupTitle,
  GroupFieldCount,
  FieldList,
  SaveRow,
  RightPanel,
  LoadingWrapper,
} from "./styles";

const { Title, Text } = Typography;

function Configuration() {
  const [themeColors, setThemeColors] = useState<ThemeColors>(initialTheme);
  const [api, contextHolder] = notification.useNotification();

  const { data, isLoading, error } = useKioskConfiguration();
  const saveMutation = useSaveKioskConfiguration();

  useEffect(() => {
    if (data) setThemeColors(data);
  }, [data]);

  useEffect(() => {
    if (error) {
      api.error({
        message: "Error fetching configuration",
        description: (error as any)?.response?.data?.message || error.message,
      });
    }
  }, [error, api]);

  const handleColorChange = (key: keyof ThemeColors, value: string) => {
    setThemeColors((prev) => ({ ...prev, [key]: value }));
  };

  const handleClear = (key: keyof ThemeColors) => {
    setThemeColors((prev) => ({ ...prev, [key]: null }));
  };

  const handleSave = () => {
    saveMutation.mutate(themeColors, {
      onSuccess: () => {
        api.success({ message: "Theme configuration saved." });
      },
      onError: (err: any) => {
        api.error({
          message: "Error saving configuration",
          description: err?.response?.data?.message || err.message,
        });
      },
    });
  };

  return (
    <PageWrapper>
      {contextHolder}

      <LeftPanel>
        <PanelHeader>
          <Title level={4} style={{ margin: 0 }}>
            Kiosk Theme Configuration
          </Title>
          <Text style={{ fontSize: 13, color: "#64748b" }}>
            Customize per-merchant theme colors. Colors left unset fall back to
            the kiosk app's built-in defaults.
          </Text>
        </PanelHeader>

        <ScrollableGroups>
          {isLoading ? (
            <LoadingWrapper>
              <Spin size="large" />
            </LoadingWrapper>
          ) : COLOR_GROUPS.map((group) => (
            <GroupCard key={group.title}>
              <GroupHeader $accent={group.accent}>
                <GroupAccentBar $color={group.accent} />
                <GroupTitle>{group.title}</GroupTitle>
                <GroupFieldCount>{group.fields.length} colors</GroupFieldCount>
              </GroupHeader>
              <FieldList>
                {group.fields.map(({ key, label, description }) => (
                  <ColorField
                    key={key}
                    fieldKey={key}
                    label={label}
                    description={description}
                    value={themeColors[key]}
                    onChange={handleColorChange}
                    onClear={handleClear}
                  />
                ))}
              </FieldList>
            </GroupCard>
          ))}
        </ScrollableGroups>

        <SaveRow>
          <Button
            type="primary"
            icon={<SaveOutlined />}
            size="large"
            loading={saveMutation.isPending}
            onClick={isLoading ? undefined : handleSave}
            style={isLoading ? { cursor: "not-allowed" } : undefined}
          >
            Save Theme Configuration
          </Button>
        </SaveRow>
      </LeftPanel>

      <RightPanel>
        <KioskPreview colors={themeColors} />
      </RightPanel>
    </PageWrapper>
  );
}

export default Configuration;
